import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { SearchIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface Manuscript {
  id: string;
  title: string;
  genre: string;
  status: string;
  wordCount: number;
  betaReaders: number;
  queries?: number;
  lastUpdated: string;
}

export const MyManuscriptsPage = () => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [manuscripts, setManuscripts] = useState<Manuscript[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fetch manuscripts on component mount
  useEffect(() => {
    fetchManuscripts();
  }, []);

  const fetchManuscripts = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(API_ENDPOINTS.manuscripts.list, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to fetch manuscripts');
        setLoading(false);
        return;
      }

      // Transform backend data to match frontend format
      const transformedManuscripts = data.manuscripts.map((m: any) => ({
        id: m.id,
        title: m.title,
        genre: formatGenre(m.primaryGenre),
        wordCount: m.wordCount,
        status: mapStatus(m.status),
        lastUpdated: getRelativeTime(m.updatedAt),
        betaReaders: m.betaApplications?.length || 0,
      }));

      setManuscripts(transformedManuscripts);
      setLoading(false);
    } catch (err) {
      console.error('Fetch manuscripts error:', err);
      setError('Network error. Please check your connection and try again.');
      setLoading(false);
    }
  };

  // Helper function to format genre enum to readable string
  const formatGenre = (genre: string): string => {
    const genreMap: Record<string, string> = {
      FANTASY: 'Fantasy',
      SCIENCE_FICTION: 'Science Fiction',
      MYSTERY: 'Mystery',
      THRILLER: 'Thriller',
      ROMANCE: 'Romance',
      LITERARY_FICTION: 'Literary Fiction',
      HISTORICAL_FICTION: 'Historical Fiction',
      HORROR: 'Horror',
      YOUNG_ADULT: 'Young Adult',
      MIDDLE_GRADE: 'Middle Grade',
      CONTEMPORARY: 'Contemporary',
      PARANORMAL: 'Paranormal',
      DYSTOPIAN: 'Dystopian',
      OTHER: 'Other',
    };
    return genreMap[genre] || genre;
  };

  // Helper function to map backend status to frontend status
  const mapStatus = (status: string): string => {
    const statusMap: Record<string, string> = {
      DRAFT: 'drafting',
      SEEKING_BETA: 'seeking',
      QUERYING: 'querying',
      PUBLISHED: 'published',
    };
    return statusMap[status] || 'drafting';
  };

  // Helper function to get relative time
  const getRelativeTime = (timestamp: string): string => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    } else {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: any; label: string }> = {
      drafting: { variant: 'drafting', label: 'Drafting' },
      seeking: { variant: 'seeking', label: 'Seeking Feedback' },
      querying: { variant: 'querying', label: 'Querying' },
    };
    const config = statusMap[status] || statusMap.drafting;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const filteredManuscripts = manuscripts.filter((m: Manuscript) => {
    const matchesSearch = m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         m.genre.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDelete = async (id: string, title: string): Promise<void> => {
    if (window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      try {
        const response = await fetch(API_ENDPOINTS.manuscripts.delete(id), {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        const data = await response.json();

        if (!response.ok) {
          alert(data.error || 'Failed to delete manuscript');
          return;
        }

        // Remove from local state
        setManuscripts(manuscripts.filter((m: Manuscript) => m.id !== id));
      } catch (err) {
        console.error('Delete manuscript error:', err);
        alert('Network error. Please try again.');
      }
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Page Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '20px',
        }}>
          <div>
            <h1 style={{
              fontSize: '40px',
              fontWeight: '400',
              marginBottom: '8px',
              color: theme.colors.textPrimary,
            }}>
              My Manuscripts
            </h1>
            <p style={{
              fontSize: '18px',
              color: theme.colors.textSecondary,
            }}>
              Manage your writing projects
            </p>
          </div>
          <Button size="large" onClick={() => window.location.href = '/writer/manuscripts/new'}>
            Add New Manuscript
          </Button>
        </div>

        {/* Search and Filter */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '32px',
          flexWrap: 'wrap',
        }}>
          {/* Search */}
          <div style={{
            flex: '1',
            minWidth: '300px',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute',
              left: '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: theme.colors.textTertiary,
            }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search manuscripts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '14px 16px 14px 48px',
                fontSize: '16px',
                border: `2px solid ${theme.colors.border}`,
                borderRadius: theme.borderRadius.large,
                fontFamily: theme.fonts.primary,
                color: theme.colors.textPrimary,
                backgroundColor: theme.colors.white,
                outline: 'none',
              }}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '14px 56px 14px 16px',
              fontSize: '16px',
              border: `2px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.large,
              fontFamily: theme.fonts.primary,
              color: theme.colors.textPrimary,
              backgroundColor: theme.colors.white,
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="all">All Status</option>
            <option value="drafting">Drafting</option>
            <option value="seeking">Seeking Feedback</option>
            <option value="querying">Querying</option>
          </select>

          {/* View Toggle */}
          <div style={{
            display: 'flex',
            gap: '8px',
            backgroundColor: theme.colors.white,
            padding: '4px',
            borderRadius: theme.borderRadius.medium,
            border: `2px solid ${theme.colors.border}`,
          }}>
            <button
              onClick={() => setViewMode('grid')}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: theme.borderRadius.small,
                backgroundColor: viewMode === 'grid' ? theme.colors.primary : 'transparent',
                color: viewMode === 'grid' ? theme.colors.white : theme.colors.textSecondary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '10px 16px',
                border: 'none',
                borderRadius: theme.borderRadius.small,
                backgroundColor: viewMode === 'list' ? theme.colors.primary : 'transparent',
                color: viewMode === 'list' ? theme.colors.white : theme.colors.textSecondary,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              List
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: theme.colors.error,
            }}>
              <p style={{
                fontSize: '18px',
                marginBottom: '20px',
              }}>
                {error}
              </p>
              <Button onClick={fetchManuscripts}>
                Try Again
              </Button>
            </div>
          </Card>
        )}

        {/* Loading State */}
        {loading && !error && (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
              }}>
                Loading manuscripts...
              </p>
            </div>
          </Card>
        )}

        {/* Manuscripts Grid/List */}
        {!loading && !error && filteredManuscripts.length === 0 ? (
          <Card>
            <div style={{
              textAlign: 'center',
              padding: '40px',
            }}>
              <p style={{
                fontSize: '18px',
                color: theme.colors.textSecondary,
                marginBottom: '20px',
              }}>
                {searchQuery || statusFilter !== 'all'
                  ? 'No manuscripts match your search criteria'
                  : 'You haven\'t added any manuscripts yet'}
              </p>
              {!searchQuery && statusFilter === 'all' && (
                <Button onClick={() => window.location.href = '/writer/manuscripts/new'}>
                  Add Your First Manuscript
                </Button>
              )}
            </div>
          </Card>
        ) : !loading && !error ? (
          <div style={{
            display: viewMode === 'grid' ? 'grid' : 'flex',
            gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(350px, 1fr))' : 'none',
            flexDirection: viewMode === 'list' ? 'column' : undefined,
            gap: '24px',
          }}>
            {filteredManuscripts.map((manuscript: Manuscript) => (
              <Card key={manuscript.id}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                }}>
                  {getStatusBadge(manuscript.status)}
                  <div style={{
                    fontSize: '13px',
                    color: theme.colors.textTertiary,
                    fontFamily: theme.fonts.monospace,
                  }}>
                    Updated {manuscript.lastUpdated}
                  </div>
                </div>

                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: theme.colors.textPrimary,
                  cursor: 'pointer',
                }}
                onClick={() => window.location.href = `/writer/manuscripts/${manuscript.id}`}
                >
                  {manuscript.title}
                </h3>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  marginBottom: '16px',
                  fontSize: '14px',
                  color: theme.colors.textSecondary,
                  fontFamily: theme.fonts.monospace,
                }}>
                  <span>{manuscript.genre}</span>
                  <span>•</span>
                  <span>{manuscript.wordCount.toLocaleString()} words</span>
                </div>

                {manuscript.betaReaders > 0 && (
                  <div style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '16px',
                  }}>
                    {manuscript.betaReaders} beta reader{manuscript.betaReaders !== 1 ? 's' : ''}
                  </div>
                )}

                {manuscript.queries && manuscript.queries > 0 && (
                  <div style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '16px',
                  }}>
                    {manuscript.queries} quer{manuscript.queries !== 1 ? 'ies' : 'y'} sent
                  </div>
                )}

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  paddingTop: '16px',
                  borderTop: `1px solid ${theme.colors.border}`,
                }}>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => window.location.href = `/writer/manuscripts/${manuscript.id}/edit`}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => window.location.href = `/writer/manuscripts/${manuscript.id}`}
                  >
                    View Details
                  </Button>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => handleDelete(manuscript.id, manuscript.title)}
                    style={{ color: theme.colors.error }}
                  >
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : null}
      </main>

      <Footer />
    </div>
  );
};
